<template>
    <div class="vx-list-aggregator">
        <h4 v-if="title">{{ title }}</h4>
        <vx-elastic-suggest
            v-bind="$attrs"
            ref="search_box"
            class="mt-3"
            :label="label"
            :search_groups="localSearchGroups"
            :batchSize="20"
            :listenFor="50"
            :show-on-focus="showOnFocus"
            :placeholder="placeholder || 'Type to search...'"
            :select-like="false"
            :list-icon="getListIcon"
            @input="add"
            @retrieving="loading = true"
        />

        <vx-scroll-area ref="entry_list" class="mb-2" :style="scrollHeight">
            <transition-group
                name="slide-fade-right"
                appear
                tag="div"
                class="h-full overflow-x-visible"
            >
                <template v-for="(entry, index) in value">
                    <div :key="getKey(entry)" class="flex pl-2">
                        <slot
                            name="entry"
                            :entry="entry"
                            :index="index"
                            :remove="() => remove(entry)"
                        >
                            <span class="entry">
                                <vx-action-icon
                                    class="list-icon"
                                    :icon="getListIcon(entry)"
                                    hover-icon="XIcon"
                                    hover-color="danger"
                                    tooltip="Remove"
                                    tooltip-position="left"
                                    @click="remove(entry)"
                                ></vx-action-icon>
                                <span class="text-lg">{{ getEntryDisplayText(entry) }} </span>
                            </span>
                        </slot>
                    </div>
                </template>
                <div key="_append_">
                    <slot name="append">
                        <span
                            v-if="addText"
                            class="entry cursor-pointer placeholder"
                            @click="focus"
                        >
                            <vx-action-icon icon="PlusIcon" hover-color="primary"></vx-action-icon>
                            <span class="text-lg ml-3">{{ addText }}</span>
                        </span>
                    </slot>
                </div>
            </transition-group>
        </vx-scroll-area>
    </div>
</template>

<script>
export default {
    name: 'vx-list-aggregator',
    props: {
        title: {
            type: String
        },
        label: {
            type: String
        },
        value: {
            type: Array,
            default: () => []
        },
        search_groups: {
            type: Object,
            required: true
        },
        displayKey: {
            type: [String, Function]
        },
        listIcon: {
            type: [String, Function],
            default: 'ChevronRightIcon'
        },
        addText: {
            type: String
        },
        allowDuplicates: {
            type: Boolean,
            default: false
        },
        idKey: {
            type: [String, Function],
            required: true
        },
        showOnFocus: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String
        },
        noScroll: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        localSearchGroups() {
            var newGroups = {}

            Object.keys(this.search_groups).forEach((groupName) => {
                var grp = this.search_groups[groupName]
                if (grp.data) {
                    newGroups[groupName] = {
                        data: grp.data.where((entry) => !this.value.includes(entry)),
                        idfield: grp.idfield,
                        searchOn: grp.searchOn,
                        displayText: this.getName
                    }
                } else {
                    newGroups[groupName] = { ...grp, displayText: this.getName }
                }
            })

            return newGroups
        },
        searchBox() {
            return this.$refs.search_box
        },
        getKey() {
            if (typeof this.idKey === 'function') {
                return this.idKey
            } else if (this.idKey) {
                return (entry) => entry[this.idKey]
            }
        },
        getListIcon() {
            if (typeof this.listIcon === 'function') {
                return this.listIcon
            }
            return (entry) => this.listIcon
        },
        getName() {
            if (typeof this.displayKey === 'function') {
                return this.displayKey
            } else if (this.displayKey) {
                return (entry) => entry[this.displayKey]
            }
            return (entry) => entry
        },
        scrollHeight() {
            var styles = ''
            if (this.title) {
                styles += 'height: calc(100% - 0.75rem - 79.58px);'
            }
            if (this.noScroll) {
                styles += 'overflow: visible !important;'
            }
            if (styles) {
                return styles
            }
            return 'height: calc(100% - 0.75rem - 57.58px)'
        }
    },
    methods: {
        startLoading() {
            this.loading = true
        },
        focus() {
            if (this.$listeners['add-new']) {
                this.$emit('add-new')
            } else {
                this.searchBox.focus()
            }
        },
        add(val) {
            this.loading = false
            if (this.allowDuplicates || !this.isDuplicate(val)) {
                this.value.push(val)
                this.$emit('input', this.value)
                if (this.noScroll) {
                    return
                }
                this.$nextTick(
                    function () {
                        if (this.$refs.entry_list) {
                            this.$refs.entry_list.scrollToBottom()
                        }
                    }.bind(this)
                )
            }
        },
        remove(val) {
            this.value.remove(val)
            this.$emit('input', this.value)
        },
        getEntryDisplayText(entry) {
            return this.getName(entry)
        },
        isDuplicate(entry) {
            var id = this.getKey(entry)
            return this.value.any((e) => this.getKey(e) == id)
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.entry {
    display: flex;
    margin-bottom: 0.75rem;
    width: 100%;
    white-space: nowrap;
}

.placeholder {
    color: rgba(0, 0, 0, 0.4);
}

.card ::v-deep .vx-card__body {
    padding: 0;
}

.list {
    height: calc(100% - 0.75rem - 79.58px);
}

.list-icon {
    margin-right: 0.75rem;
    &:hover {
        margin-right: 1.25rem;
    }
}

.theme-dark {
    .vx-list-aggregator {
        span {
            color: $theme-dark-text-color;
        }
    }
}
</style>
